import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIsAdmin } from '@hooks/usePermission';

import { useEmployeeListRoster } from '../../../hooks/useEmployeeListRoster';
import {
  connectSageHR,
  disconnectSageHR,
  getSageHREmployees,
  autoMapSageHREmployees,
  saveSageHRMappings,
  syncSageHRTimeRange,
  exportAllSageHREmployees,
  previewSageHRImport,
  importSageHREmployees,
  subscribeToSageHRImportLogs,
  getSageHRImportLogDetails,
  subscribeToSageHRStatus,
} from '../../../services/sagehr';
import { ImportPreviewModal } from './ImportPreviewModal.component';
import {
  addNotification,
  notificationStates,
} from '../../../components/Notification';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants, buttonSizes } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import styles from './QuickBooksCard.module.scss';
import connectFormStyles from './KekaConnectForm.module.scss';

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function fmtDate(dateObj) {
  if (!dateObj) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(dateObj);
}

const EMPTY_FORM = { subdomain: '', apiKey: '' };

export const SageHRCard = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeTeamId = useSelector(
    (s) => s.team?.active?.activeTeamid ?? s.team?.active?.activeTeamId
  );

  const companyId = userProfile?.activeCompany?.companyId;
  const userId = userProfile?.id;
  const isAdmin = useIsAdmin();
  const teamId = activeTeamId;

  const { employees: prodaffEmployees, isLoading: rosterLoading } =
    useEmployeeListRoster(teamId);

  const [sageHRStatus, setSageHRStatus] = useState({
    connected: false,
    subdomain: null,
    connectedAt: null,
    connectedBy: null,
    employeeMappings: {},
    employeeMappingsUpdatedAt: null,
    lastSyncAt: null,
    lastSyncStats: null,
  });

  const [sageHREmployees, setSageHREmployees] = useState([]);
  const [sageHREmployeesLoading, setSageHREmployeesLoading] = useState(false);
  const [pendingMappings, setPendingMappings] = useState({});

  const [connectForm, setConnectForm] = useState(EMPTY_FORM);
  const [showApiKey, setShowApiKey] = useState(false);

  const [syncRange, setSyncRange] = useState({
    start: firstOfMonthStr(),
    end: todayStr(),
  });
  const [syncing, setSyncing] = useState(false);

  const [importLogs, setImportLogs] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsTitle, setDetailsTitle] = useState('');

  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);

  // ── Firestore real-time status + import logs ────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const unsub = subscribeToSageHRStatus(companyId, setSageHRStatus);
    return unsub;
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !sageHRStatus.connected) return;
    const unsub = subscribeToSageHRImportLogs(companyId, setImportLogs);
    return unsub;
  }, [companyId, sageHRStatus.connected]);

  // ── Load Sage HR employees when connected ───────────────────────────────────
  useEffect(() => {
    if (!sageHRStatus.connected || !companyId) {
      setSageHREmployees([]);
      return;
    }
    setSageHREmployeesLoading(true);
    getSageHREmployees(companyId)
      .then(({ employees }) => setSageHREmployees(employees || []))
      .catch(() =>
        addNotification({
          message: 'Failed to load Sage HR employees.',
          level: notificationStates.error,
        })
      )
      .finally(() => setSageHREmployeesLoading(false));
  }, [sageHRStatus.connected, companyId]);

  const effectiveMappings = useMemo(() => {
    const base = sageHRStatus.employeeMappings || {};
    const merged = { ...base };
    for (const [uid, val] of Object.entries(pendingMappings)) {
      if (val === null) delete merged[uid];
      else merged[uid] = val;
    }
    return merged;
  }, [sageHRStatus.employeeMappings, pendingMappings]);

  const hasPendingChanges = Object.keys(pendingMappings).length > 0;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleConnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can connect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    const { subdomain, apiKey } = connectForm;
    if (!subdomain.trim() || !apiKey.trim()) {
      addNotification({
        message: 'Please fill in all fields.',
        level: notificationStates.error,
      });
      return;
    }
    setConnecting(true);
    try {
      await connectSageHR(companyId, userId, subdomain.trim(), apiKey.trim());
      setConnectForm(EMPTY_FORM);
      addNotification({
        message: 'Sage HR connected successfully.',
        level: notificationStates.success,
      });
    } catch (err) {
      addNotification({
        message:
          err?.response?.data?.message ||
          'Failed to connect to Sage HR. Please check your credentials.',
        level: notificationStates.error,
      });
    } finally {
      setConnecting(false);
    }
  }, [companyId, userId, isAdmin, connectForm]);

  const handleDisconnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can disconnect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    setDisconnecting(true);
    try {
      await disconnectSageHR(companyId, userId);
      setPendingMappings({});
      addNotification({
        message: 'Sage HR disconnected.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to disconnect Sage HR. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setDisconnecting(false);
    }
  }, [companyId, userId, isAdmin]);

  const handleAutoMap = useCallback(async () => {
    setAutoMapping(true);
    try {
      const { mapped, unmatched } = await autoMapSageHREmployees(
        companyId,
        teamId
      );
      setPendingMappings({});
      const total = mapped + (unmatched?.length ?? 0);
      addNotification({
        message: `Auto-mapped ${mapped} of ${total} employees by email.`,
        level:
          mapped > 0 ? notificationStates.success : notificationStates.warning,
      });
    } catch {
      addNotification({
        message: 'Auto-mapping failed. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setAutoMapping(false);
    }
  }, [companyId, teamId]);

  const handleSelectMapping = useCallback(
    (prodaffUserId, sageHREmployeeId) => {
      if (!sageHREmployeeId) {
        setPendingMappings((prev) => ({ ...prev, [prodaffUserId]: null }));
        return;
      }
      const emp = sageHREmployees.find(
        (e) => String(e.id) === String(sageHREmployeeId)
      );
      if (!emp) return;
      setPendingMappings((prev) => ({
        ...prev,
        [prodaffUserId]: {
          sageHREmployeeId: emp.id,
          sageHREmployeeName: emp.name,
        },
      }));
    },
    [sageHREmployees]
  );

  const handleUnmap = useCallback((prodaffUserId) => {
    setPendingMappings((prev) => ({ ...prev, [prodaffUserId]: null }));
  }, []);

  const handleSaveMappings = useCallback(async () => {
    if (!hasPendingChanges) return;
    setSavingMappings(true);
    try {
      await saveSageHRMappings(companyId, pendingMappings);
      setPendingMappings({});
      addNotification({
        message: 'Employee mappings saved.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to save mappings. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSavingMappings(false);
    }
  }, [companyId, pendingMappings, hasPendingChanges]);

  const handleExportAll = useCallback(async () => {
    setExportingAll(true);
    try {
      const { mapped, created, skipped, errors } =
        await exportAllSageHREmployees(companyId, teamId);
      setPendingMappings({});
      const parts = [];
      if (created > 0) parts.push(`${created} created`);
      if (mapped > 0) parts.push(`${mapped} matched by email`);
      if (skipped > 0) parts.push(`${skipped} already mapped`);
      if (errors?.length > 0) parts.push(`${errors.length} failed`);
      addNotification({
        message: `Export complete: ${parts.join(', ') || 'nothing to do'}.`,
        level:
          errors?.length > 0
            ? notificationStates.warning
            : notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to export employees. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setExportingAll(false);
    }
  }, [companyId, teamId]);

  const handleOpenImportPreview = useCallback(async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    setImportResults(null);
    try {
      const data = await previewSageHRImport(companyId, teamId, userId);
      setPreviewData(data);
    } catch {
      addNotification({
        message: 'Failed to load import preview. Please try again.',
        level: notificationStates.error,
      });
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }, [companyId, teamId, userId]);

  const handleClosePreviewModal = useCallback(() => {
    if (importing) return;
    setPreviewOpen(false);
    setPreviewData(null);
    setImportResults(null);
  }, [importing]);

  const handleConfirmImport = useCallback(async () => {
    setImporting(true);
    try {
      const result = await importSageHREmployees(companyId, teamId, userId);
      setImportResults(result);
    } catch {
      addNotification({
        message: 'Failed to import employees from Sage HR. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setImporting(false);
    }
  }, [companyId, teamId, userId]);

  const handleViewDetails = useCallback(
    async (log) => {
      setDetailsLoading(true);
      setDetailsOpen(true);
      setDetailsData(null);
      setDetailsTitle(`Sage HR Import · ${fmtDate(log.importedAt)}`);
      try {
        const detail = await getSageHRImportLogDetails(companyId, log.id);
        setDetailsData({
          ...log.stats,
          employeeResults: detail.employees || [],
        });
      } catch {
        addNotification({
          message: 'Failed to load import details.',
          level: notificationStates.error,
        });
        setDetailsOpen(false);
      } finally {
        setDetailsLoading(false);
      }
    },
    [companyId]
  );

  const handleSync = useCallback(async () => {
    if (!syncRange.start || !syncRange.end) {
      addNotification({
        message: 'Please select a valid date range.',
        level: notificationStates.error,
      });
      return;
    }
    setSyncing(true);
    try {
      const result = await syncSageHRTimeRange(
        companyId,
        teamId,
        syncRange.start,
        syncRange.end
      );
      addNotification({
        message: `Sync complete. ${result.synced ?? 0} records synced.`,
        level: notificationStates.success,
      });
    } catch (err) {
      addNotification({
        message:
          err?.response?.data?.message || 'Sync failed. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSyncing(false);
    }
  }, [companyId, teamId, syncRange]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <ImportPreviewModal
        isOpen={previewOpen}
        onClose={handleClosePreviewModal}
        onConfirm={handleConfirmImport}
        preview={previewData}
        loading={previewLoading}
        confirming={importing}
        source="Sage HR"
        results={importResults}
      />
      <ImportPreviewModal
        isOpen={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setDetailsData(null);
        }}
        preview={null}
        loading={detailsLoading}
        confirming={false}
        source="Sage HR"
        results={detailsData}
        resultsTitle={detailsTitle}
      />
      {/* Connection status panel */}
      <div className={styles.statusPanel}>
        <div className={styles.statusLeft}>
          <span
            className={`${styles.badge} ${sageHRStatus.connected ? styles.connected : styles.disconnected}`}
          >
            {sageHRStatus.connected ? 'Connected' : 'Not Connected'}
          </span>
          {sageHRStatus.connected ? (
            <span className={styles.connectedMeta}>
              {sageHRStatus.subdomain &&
                `Workspace: ${sageHRStatus.subdomain}.sage.hr`}
              {sageHRStatus.connectedAt &&
                ` · Connected ${fmtDate(sageHRStatus.connectedAt)}`}
            </span>
          ) : (
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              Connect your Sage HR account to start syncing attendance data.
            </Text>
          )}
        </div>
        {isAdmin && sageHRStatus.connected && (
          <Button
            text={disconnecting ? 'Disconnecting…' : 'Disconnect'}
            variant={buttonVariants.secondary}
            size={buttonSizes.sm}
            onClick={handleDisconnect}
            disabled={disconnecting}
          />
        )}
      </div>

      {/* Connect form — shown to admins when not connected */}
      {!sageHRStatus.connected && isAdmin && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Connect Sage HR</span>
              <span className={styles.sectionSubtitle}>
                Enter your Sage HR subdomain and API key from Settings › API.
              </span>
            </div>
          </div>
          <div className={styles.sectionBody}>
            <div className={connectFormStyles.form}>
              <div className={connectFormStyles.field}>
                <label htmlFor="sagehr-subdomain">Subdomain</label>
                <input
                  id="sagehr-subdomain"
                  type="text"
                  placeholder="e.g. prodaff  (not prodaff.sage.hr)"
                  value={connectForm.subdomain}
                  onChange={(e) =>
                    setConnectForm((f) => ({
                      ...f,
                      subdomain: e.target.value,
                    }))
                  }
                  disabled={connecting}
                  autoComplete="off"
                />
                <span className={connectFormStyles.hint}>
                  Only the subdomain — if your URL is prodaff.sage.hr, enter
                  just <strong>prodaff</strong>
                </span>
              </div>
              <div className={connectFormStyles.field}>
                <label htmlFor="sagehr-api-key">
                  API Key
                  <button
                    type="button"
                    className={connectFormStyles.toggleSecret}
                    onClick={() => setShowApiKey((v) => !v)}
                  >
                    {showApiKey ? 'Hide' : 'Show'}
                  </button>
                </label>
                <input
                  id="sagehr-api-key"
                  type={showApiKey ? 'text' : 'password'}
                  placeholder="API key from Sage HR Settings › API"
                  value={connectForm.apiKey}
                  onChange={(e) =>
                    setConnectForm((f) => ({ ...f, apiKey: e.target.value }))
                  }
                  disabled={connecting}
                  autoComplete="new-password"
                />
                <span className={connectFormStyles.hint}>
                  Found in your Sage HR admin panel under Settings › API
                </span>
              </div>
              <div className={connectFormStyles.actions}>
                <Button
                  text={connecting ? 'Connecting…' : 'Connect Sage HR'}
                  variant={buttonVariants.primary}
                  size={buttonSizes.sm}
                  onClick={handleConnect}
                  disabled={connecting}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employee Mapping panel */}
      {sageHRStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Employee Mapping</span>
              <span className={styles.sectionSubtitle}>
                Map Prodaff employees to their Sage HR employee records.
              </span>
            </div>
            <div className={styles.sectionActions}>
              <Button
                text={autoMapping ? 'Mapping…' : 'Auto-map by Email'}
                variant={buttonVariants.secondary}
                size={buttonSizes.sm}
                onClick={handleAutoMap}
                disabled={
                  autoMapping ||
                  exportingAll ||
                  importing ||
                  sageHREmployeesLoading
                }
              />
              <Button
                text={exportingAll ? 'Exporting…' : 'Export All to Sage HR'}
                variant={buttonVariants.secondary}
                size={buttonSizes.sm}
                onClick={handleExportAll}
                disabled={
                  exportingAll ||
                  autoMapping ||
                  importing ||
                  sageHREmployeesLoading
                }
              />
              <Button
                text={importing ? 'Importing…' : 'Import from Sage HR'}
                variant={buttonVariants.primary}
                size={buttonSizes.sm}
                onClick={handleOpenImportPreview}
                disabled={
                  importing ||
                  autoMapping ||
                  exportingAll ||
                  sageHREmployeesLoading
                }
              />
            </div>
          </div>

          <div className={styles.sectionBody}>
            {rosterLoading || sageHREmployeesLoading ? (
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                color="gray-500"
              >
                Loading…
              </Text>
            ) : prodaffEmployees.length === 0 ? (
              <EmptyState
                illustration
                title="No Employees Found"
                description="No employees found in this department to map to Sage HR."
              />
            ) : (
              <>
                <table className={styles.mappingTable}>
                  <thead>
                    <tr>
                      <th>Prodaff Employee</th>
                      <th>Sage HR Employee</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prodaffEmployees.map((emp) => {
                      const mapping = effectiveMappings[emp.userId];
                      return (
                        <tr key={emp.userId}>
                          <td>
                            <div className={styles.employeeName}>
                              {emp.name}
                            </div>
                            <div className={styles.employeeEmail}>
                              {emp.email}
                            </div>
                          </td>
                          <td>
                            {mapping ? (
                              <div className={styles.mappingCell}>
                                <span className={styles.mappedTag}>
                                  {mapping.sageHREmployeeName ||
                                    mapping.sageHREmployeeId}
                                  <button
                                    className={styles.unmapBtn}
                                    onClick={() => handleUnmap(emp.userId)}
                                    title="Remove mapping"
                                  >
                                    ×
                                  </button>
                                </span>
                              </div>
                            ) : (
                              <select
                                className={styles.qbSelect}
                                value=""
                                onChange={(e) =>
                                  handleSelectMapping(
                                    emp.userId,
                                    e.target.value
                                  )
                                }
                                disabled={sageHREmployees.length === 0}
                              >
                                <option value="">
                                  — Select Sage HR employee —
                                </option>
                                {sageHREmployees.map((e) => (
                                  <option key={e.id} value={e.id}>
                                    {e.name}
                                    {e.email ? ` · ${e.email}` : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            <div className={styles.actionCell} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className={styles.saveMappingsRow}>
                  {hasPendingChanges && (
                    <span className={styles.pendingNote}>Unsaved changes</span>
                  )}
                  <Button
                    text={savingMappings ? 'Saving…' : 'Save Mappings'}
                    variant={buttonVariants.primary}
                    size={buttonSizes.sm}
                    onClick={handleSaveMappings}
                    disabled={savingMappings || !hasPendingChanges}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Import History panel */}
      {sageHRStatus.connected && importLogs.length > 0 && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Import History</span>
              <span className={styles.sectionSubtitle}>
                Last {importLogs.length} import run
                {importLogs.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className={styles.sectionBody}>
            {importLogs.map((log) => (
              <div key={log.id} className={styles.historyRow}>
                <div className={styles.historyInfo}>
                  <span className={styles.historyDate}>
                    {fmtDate(log.importedAt)}
                  </span>
                  <span className={styles.historyMeta}>
                    by{' '}
                    {log.importedBy?.name ||
                      log.importedBy?.userId ||
                      'Unknown'}
                  </span>
                </div>
                <div className={styles.historyStats}>
                  {(log.stats?.imported || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipGreen}`}>
                      {log.stats.imported} created
                    </span>
                  )}
                  {(log.stats?.invited || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipBlue}`}>
                      {log.stats.invited} invited
                    </span>
                  )}
                  {(log.stats?.mapped || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipGray}`}>
                      {log.stats.mapped} linked
                    </span>
                  )}
                  {(log.stats?.errors || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipRed}`}>
                      {log.stats.errors} failed
                    </span>
                  )}
                  {(log.stats?.imported || 0) === 0 &&
                    (log.stats?.invited || 0) === 0 &&
                    (log.stats?.mapped || 0) === 0 &&
                    (log.stats?.errors || 0) === 0 && (
                      <span className={`${styles.chip} ${styles.chipGray}`}>
                        nothing to import
                      </span>
                    )}
                </div>
                <Button
                  text="Details"
                  variant={buttonVariants.secondary}
                  size={buttonSizes.sm}
                  onClick={() => handleViewDetails(log)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync panel */}
      {sageHRStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Sync Timesheets</span>
              <span className={styles.sectionSubtitle}>
                Push Prodaff tracked hours to Sage HR as timesheet entries for a
                selected date range.
              </span>
            </div>
          </div>

          <div className={styles.sectionBody}>
            <div className={styles.syncRow}>
              <div className={styles.dateField}>
                <label htmlFor="sagehr-start-date">From</label>
                <input
                  id="sagehr-start-date"
                  type="date"
                  value={syncRange.start}
                  max={syncRange.end || todayStr()}
                  onChange={(e) =>
                    setSyncRange((r) => ({ ...r, start: e.target.value }))
                  }
                  disabled={syncing}
                />
              </div>
              <div className={styles.dateField}>
                <label htmlFor="sagehr-end-date">To</label>
                <input
                  id="sagehr-end-date"
                  type="date"
                  value={syncRange.end}
                  min={syncRange.start}
                  max={todayStr()}
                  onChange={(e) =>
                    setSyncRange((r) => ({ ...r, end: e.target.value }))
                  }
                  disabled={syncing}
                />
              </div>
              <Button
                text={syncing ? 'Syncing…' : 'Sync Now'}
                variant={buttonVariants.primary}
                size={buttonSizes.sm}
                onClick={handleSync}
                disabled={syncing || !syncRange.start || !syncRange.end}
              />
            </div>

            {sageHRStatus.lastSyncAt && (
              <div className={styles.syncStats}>
                <div className={styles.syncStatsTitle}>
                  Last sync · {fmtDate(sageHRStatus.lastSyncAt)}
                  {sageHRStatus.lastSyncStats?.startDate &&
                    sageHRStatus.lastSyncStats?.endDate && (
                      <span style={{ fontWeight: 400, color: '#64748b' }}>
                        {' '}
                        ({sageHRStatus.lastSyncStats.startDate} →{' '}
                        {sageHRStatus.lastSyncStats.endDate})
                      </span>
                    )}
                </div>
                {sageHRStatus.lastSyncStats && (
                  <div className={styles.syncStatsGrid}>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Synced</span>
                      <span className={`${styles.statValue} ${styles.success}`}>
                        {sageHRStatus.lastSyncStats.synced ?? 0}
                      </span>
                    </div>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Skipped</span>
                      <span className={`${styles.statValue} ${styles.warning}`}>
                        {sageHRStatus.lastSyncStats.skipped ?? 0}
                      </span>
                    </div>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Errors</span>
                      <span className={`${styles.statValue} ${styles.error}`}>
                        {sageHRStatus.lastSyncStats.errors ?? 0}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
